import React, { FC, useEffect } from 'react';
import {
  BodyTextSmall,
  ButtonAsWrapper,
  DetailText,
  FlexCol,
  FlexRow,
  PaywallButton,
  TextDynamic,
} from '@minecraft.atoms';
import { getCurrentDateTime } from '@minecraft.date-utils';
import IconWithErrorBoundary from '@minecraft.icon';
import { useTranslation } from '@blocs.i18n';
import { captureHeapEvent, HEAP_EVENT_NAMES } from '@blocs.heap';
import { CheckoutProps } from '../../Checkout/types';
import { priceFormatter } from '../../helpers/priceFormatter';

const TERMS_LINK = 'https://www.castingnetworks.com/terms';
const TermsLink: FC = () => {
  const { t } = useTranslation();

  return (
    <ButtonAsWrapper
      aria-label={t('common:paywall.plan.reactivate-modal.termsOfService')}
      onClick={() => window.open(TERMS_LINK, '_blank')}
    >
      <DetailText color="lowContrastText" underline>
        {t('common:paywall.plan.reactivate-modal.termsOfService')}
      </DetailText>
    </ButtonAsWrapper>
  );
};

type Props = {
  onChangePayment: () => void;
  onCompletePurchase: CheckoutProps['onCompletePurchase'];
  selectedPlan?: CheckoutProps['selectedPlan'];
  plansLoading?: CheckoutProps['plansLoading'];
  completePurchaseLoading?: CheckoutProps['completePurchaseLoading'];
};

export const ReactivationConfirmation: FC<Props> = ({
  onChangePayment,
  onCompletePurchase,
  selectedPlan,
  plansLoading,
  completePurchaseLoading,
}) => {
  const { t, formatIntlDate } = useTranslation();

  useEffect(() => {
    captureHeapEvent({ name: HEAP_EVENT_NAMES.REACTIVATION_MODAL_VIEW });
  }, []);

  const features = [
    t('common:registration.nudge.rolesWaiting'),
    t('common:paywall.plan.reactivate-modal.unlimitedMedia'),
  ];

  const price = priceFormatter({
    price: Number(selectedPlan?.price),
    currencyCode: selectedPlan?.currencyCode,
    t,
    roundingFloor: true,
  });

  return (
    <>
      <IconWithErrorBoundary name="reactivate-modal-asset" />
      <TextDynamic align="center" fontSize="h2" fontWeight="bold" color="inverted">
        {t('common:paywall.plan.reactivate-modal.header')}
      </TextDynamic>
      <FlexCol gap="2" bgColor="maxContrast" bgOpacity={0.5} bgBlur="sm" borderRadius="md" className="cn_atom_p-4">
        {features.map((feat) => (
          <FlexRow gap="2" alignItems="center" key={feat}>
            <IconWithErrorBoundary name="check-circle" themeColor="goldPaywall" />
            <BodyTextSmall color="inverted">{feat}</BodyTextSmall>
          </FlexRow>
        ))}
      </FlexCol>
      <FlexCol w="full" justifyItems="center" alignItems="center" gap="2" data-testid="reactivation-charge-info">
        <BodyTextSmall color="inverted">
          {t('common:paywall.plan.charged_on', {
            price,
            date: formatIntlDate({ date: getCurrentDateTime(), format: 'MONTH_DAY_YEAR' }),
          })}
        </BodyTextSmall>
      </FlexCol>
      <FlexCol w="full" gap="4" mdProps={{ w: '2/3' }} alignItems="center">
        <PaywallButton
          label={t('common:paywall.plan.reactivate-modal.confirmReactivation')}
          color="gold"
          onClick={async () => {
            captureHeapEvent({ name: HEAP_EVENT_NAMES.REACTIVATION_MODAL_CTA_CLICK });
            await onCompletePurchase({ isSingleClickReactivation: true });
          }}
          full
          isSmall
          disabled={plansLoading || completePurchaseLoading}
        />
        <ButtonAsWrapper
          aria-label={t('talent:billing.card.change')}
          onClick={onChangePayment}
          disabled={plansLoading || completePurchaseLoading}
        >
          <BodyTextSmall color="goldPaywall">{t('talent:billing.card.change')}</BodyTextSmall>
        </ButtonAsWrapper>
      </FlexCol>
      <DetailText data-testid="reactivation-terms-text">
        <DetailText color="lowContrastText" italic>
          {t('common:paywall.plan.reactivate-modal.footer')}
        </DetailText>{' '}
        <TermsLink />
      </DetailText>
    </>
  );
};
